<template>
  <div class="content">
    <div class="header">
      <div class="page-title">
        <md-icon :md-src="require('../assets/svg/neoAi/magic-icon.svg')" />
        <span>Neo.ai</span>
      </div>
      <div class="clear-chat">
        <md-button
          class="md-primary md-accent-container md-dense"
          @click.stop.prevent="clearChat"
          slot="header"
        >
          <i class="md-icon">clear_all</i>
          {{ $t('clean') }}
        </md-button>
      </div>
    </div>
    <div class="chat-bubble">
      <div class="empty-chat" v-if="chat.length === 0">
        <div>
          <md-icon class="magic-icon" :md-src="require('../assets/svg/neoAi/magic-icon.svg')" />
        </div>
        <span class="chat-explanation">{{ $t('neoAiTip') }}</span>
        <div class="chat-cards">
          <div
            class="tip-card"
            v-for="tip in tips"
            :key="tip.key"
            @click="addTipToChat(tip.key)"
          >
            <md-icon :md-src="require('../assets/svg/neoAi/magic-icon.svg')" />
            <span>{{ $t(tip.key) }}</span>
          </div>
        </div>
      </div>
      <div class="chat-with-messages" v-else ref="chatContainer">
        <div
          v-for="(message, index) in chat"
          :key="index"
          :class="{
            'user-message': message.sender === SENDER_USER,
            'ai-message': message.sender === SENDER_AI
          }"
        >
          <md-card class="md-elevation-1 card">
            <md-card-content class="card-content">
              <div class="action-container">
                <div
                  class="icon-svg"
                  :class="{
                  'copied-svg': message.type === TYPE_TEXT,
                  'download-svg': isDownloadSvg(message)
                  }"
                  @click="handleIconClick(message, index)"
                  v-if="message.type !== TYPE_LOADING"
                ></div>
              </div>
              <span v-if="message.text && message.sender === SENDER_AI" class="preserve-linebreaks typewriter-text" :ref="'ai-message-' + index">
                {{ getDisplayText(message, index) }}
              </span>
              <span v-else-if="message.text && message.sender === SENDER_USER" class="preserve-linebreaks">
                {{ message.text }}
              </span>
              <span v-if="message.type === TYPE_LOADING" class="preserve-linebreaks">
                {{ $t('thinking') }}
                <span class="loading-dots">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
              <GenericChart v-if="message.type === TYPE_CHART && !message.isTyping" :ref="'chart-' + index"
                :chart-config="message.chart"
                :autoColors="true"/>
              <SummaryTable
                v-if="message.type === TYPE_TABLE && !message.isTyping"
                :data-item="message.tableData.dataItem"
                :labels="message.tableData.labels"
                :datasets="message.tableData.datasets"
                :max-rows="message.tableData.maxRows"
                :ref="'table-' + index"
              />
              <DataTable
                v-if="message.type === TYPE_DATA && !message.isTyping"
                :labels="message.tableData.labels"
                :datasets="message.tableData.datasets"
                :ref="'data-' + index"
              />
            </md-card-content>
          </md-card>
        </div>
      </div>
    </div>
    <div class="chat">
      <textarea
        ref="textInput"
        @keydown.enter.exact.prevent="sendMessage"
        :placeholder="$t('neoAiTextInputPlaceholder')"
        class="text-input"
      ></textarea>
      <i class="md-icon send-message" @click="sendMessageFromButton">send</i>
    </div>
  </div>
</template>

<script>
import GenericChart from '../components/Chart/GenericChart.vue'
import SummaryTable from '../components/Chart/SummaryTable.vue'
import DataTable from '../components/Chart/DataTable.vue'
import { mapActions } from 'vuex'

export default {
  name: 'NeoAi',
  components: {
    GenericChart,
    SummaryTable,
    DataTable
  },
  data: () => ({
    chat: [],
    tips: [
      { key: 'neoAiFirstTip' },
      { key: 'neoAiSecondTip' },
      { key: 'neoAiThirdTip' },
      { key: 'neoAiFourthTip' }
    ],
    SENDER_USER: 'user',
    SENDER_AI: 'ai',
    TYPE_TEXT: 'text',
    TYPE_CHART: 'data',
    TYPE_TABLE: 'table',
    TYPE_DATA: 'data-table',
    TYPE_LOADING: 'loading',
    sessionId: null,
    isLoading: false,
    typewriterIntervals: {}
  }),
  computed: {
    isDownloadSvg() {
      return (message) => {
        return message.type === this.TYPE_CHART || message.type === this.TYPE_TABLE || message.type === this.TYPE_DATA
      }
    }
  },
  methods: {
    ...mapActions('Chat', ['sendChatMessage']),
    getDisplayText(message) {
      if (!message.displayText) {
        return ''
      }
      return message.displayText
    },
    startTypewriterAnimation(message, messageIndex, onComplete = null, skipTypewriter = false) {
      if (!message.text || message.sender !== this.SENDER_AI) {
        return
      }

      this.$set(message, 'displayText', skipTypewriter ? message.text : '')
      this.$set(message, 'isTyping', !skipTypewriter)
      this.$set(message, 'currentIndex', skipTypewriter ? message.text.length : 0)

      const finishAnimation = () => {
        message.isTyping = false
        delete this.typewriterIntervals[messageIndex]

        this.$nextTick(() => {
          this.scrollToBottom()
          if (onComplete && typeof onComplete === 'function') {
            onComplete()
          }
        })

        this.$forceUpdate()
      }

      if (skipTypewriter) {
        finishAnimation()
        return
      }

      const fullText = message.text
      const typingSpeed = 30

      const typeWriter = () => {
        if (message.currentIndex < fullText.length) {
          message.displayText += fullText.charAt(message.currentIndex)
          message.currentIndex++

          this.$nextTick(() => {
            this.scrollToBottom()
          })

          this.typewriterIntervals[messageIndex] = setTimeout(typeWriter, typingSpeed)
        } else {
          finishAnimation()
        }
      }

      typeWriter()
    },
    stopTypewriterAnimation(messageIndex) {
      if (this.typewriterIntervals[messageIndex]) {
        clearTimeout(this.typewriterIntervals[messageIndex])
        delete this.typewriterIntervals[messageIndex]
      }
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    clearChat() {
      Object.keys(this.typewriterIntervals).forEach(key => {
        this.stopTypewriterAnimation(key)
      })

      this.chat = []
      this.sessionId = null
    },
    addTipToChat(tip) {
      const textarea = this.$refs.textInput

      if (textarea) {
        textarea.value = this.$t(tip)
        this.sendMessage({ target: textarea }, tip)
      }
    },
    addDefaultResponse(skipTypewriter = false) {
      const messageIndex = this.chat.length
      const message = {
        text: this.$t('defaultChatMessage'),
        sender: this.SENDER_AI,
        type: this.TYPE_TEXT
      }

      this.chat.push(message)

      this.$nextTick(() => {
        this.startTypewriterAnimation(message, messageIndex, null, skipTypewriter)
      })
    },
    addLoadingMessage() {
      this.chat.push({
        text: '',
        sender: this.SENDER_AI,
        type: this.TYPE_LOADING
      })
      this.isLoading = true
    },
    removeLoadingMessage() {
      const loadingIndex = this.chat.findIndex(message => message.type === this.TYPE_LOADING)

      if (loadingIndex !== -1) {
        this.chat.splice(loadingIndex, 1)
      }
      this.isLoading = false
    },
    isValidMessage(message) {
      return message && message.trim() !== ''
    },
    processMessageResponse(response, skipTypewriter = false) {
      if (!response.text?.trim() && !response.chart && !response.data) {
        this.addDefaultResponse(skipTypewriter)
        return
      }

      if (response.text === "" && response.chart === null) {
        const messageIndex = this.chat.length
        const message = {
          text: this.$t('noDataMessage'),
          sender: this.SENDER_AI,
          type: this.TYPE_TEXT
        }

        this.chat.push(message)

        this.$nextTick(() => {
          this.startTypewriterAnimation(message, messageIndex, null, skipTypewriter)
        })
        return
      }

      const messageIndex = this.chat.length
      const message = {
        text: response.text,
        sender: this.SENDER_AI,
        type: this.TYPE_TEXT
      }

      if (response.text?.trim()) {
        this.chat.push(message)
      }

      this.$nextTick(() => {
          if (response.chart) {
            this.startTypewriterAnimation(message, messageIndex, () => {
              this.buildChartMessages(response)
            }, skipTypewriter)
          } else if (response.data) {
            this.startTypewriterAnimation(message, messageIndex, () => {
              this.buildDataMessages(response)
            }, skipTypewriter)
          } else {
            this.startTypewriterAnimation(message, messageIndex, null, skipTypewriter)
          }
        })
    },

    async sendMessage(event, tipKey = null) {
      const message = event.target.value

      if (!this.isValidMessage(message)) {
        return
      }

      if (this.isLoading) {
        return
      }

      this.chat.push({
        text: message,
        sender: this.SENDER_USER,
        type: this.TYPE_TEXT
      })

      event.target.value = ''

      this.addLoadingMessage()
      const isFirstTip = tipKey === 'neoAiFirstTip'

      try {
        const response = await this.sendChatMessage({
          userQuestion: message,
          sessionId: this.sessionId
        })

        if (response.sessionId) {
          this.sessionId = response.sessionId
        }

        this.removeLoadingMessage()
        this.processMessageResponse(response, isFirstTip)

      } catch {
        this.removeLoadingMessage()
        this.addDefaultResponse(isFirstTip)
      }
    },
    sendMessageFromButton() {
      const textarea = this.$refs.textInput

      if (textarea) {
        this.sendMessage({ target: textarea })
      }
    },
    async handleIconClick(message, index) {
      switch (message.type) {
        case this.TYPE_TEXT:
          await this.handleTextIconClick(message)
          break
        case this.TYPE_CHART:
          this.handleChartIconClick(index)
          break
        case this.TYPE_TABLE:
          this.handleTableIconClick(index)
          break
        case this.TYPE_DATA:
          this.handleTableIconClick(index)
          break
        default:
      }
    },
    async handleTextIconClick(message) {
      if (message.text) {
        try {
          await navigator.clipboard.writeText(message.text)
          this.$showMessage(this.$t('copySuccess'))
        } catch {
          this.$showMessage(this.$t('copyError'))
        }
      }
    },
    handleChartIconClick(index) {
      const chartRef = this.getComponentRef('chart', index)

      if (chartRef && typeof chartRef.downloadChart === 'function') {
        chartRef.downloadChart()
      }
    },
    handleTableIconClick(index) {
      const tableRef = this.getComponentRef('table', index) || this.getComponentRef('data', index)

      if (tableRef && typeof tableRef.downloadTableAsXLS === 'function') {
        tableRef.downloadTableAsXLS()
      }
    },
    getComponentRef(type, index) {
      let ref = this.$refs[`${type}-${index}`]

      if (Array.isArray(ref)) {
        ref = ref[0]
      }

      return ref
    },
    buildChartMessages(message) {
      const tableMessageIndex = this.chat.length
      const tableMessage = {
        tableData: this.convertChartToTableFormat(message.chart),
        text: this.$t('displayingTeenResults'),
        sender: this.SENDER_AI,
        type: this.TYPE_TABLE
      }

      this.chat.push(tableMessage)

      this.$nextTick(() => {
        this.startTypewriterAnimation(tableMessage, tableMessageIndex, () => {
          this.chat.push({
            chart: message.chart,
            sender: this.SENDER_AI,
            type: this.TYPE_CHART
          })
        })
      })
    },

    buildDataMessages(message) {
      const tableMessage = {
        tableData: this.convertDataToTableFormat(message.data),
        sender: this.SENDER_AI,
        type: this.TYPE_DATA
      }

      this.chat.push(tableMessage)
    },
    convertChartToTableFormat(chart) {
      return {
        dataItem: chart.dataItem || 'Item',
        labels: chart.data.labels,
        datasets: chart.data.datasets,
        maxRows: 10
      }
    },
    convertDataToTableFormat(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        return { labels: [], datasets: [] }
      }

      const firstRow = data[0]
      const keys = Object.keys(firstRow)
      const labels = data.map((row, index) => `${index + 1}`)

      const datasets = keys.map(key => ({
        label: key,
        data: data.map(row => row[key])
      }))

      return {
        labels: labels,
        datasets: datasets
      }
    }
  },
  watch: {
    chat() {
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    }
  },
  beforeDestroy() {
    this.clearChat()
  }
}
</script>

<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;

  .header {
    width: 100%;
    flex: 1;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;

    & > * {
      height: 100%;
    }

    .page-title {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      color: #000;

      span {
        margin-left: 10px;
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: 26px;
        letter-spacing: 0.005px;
      }
    }

    .clear-chat {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .chat-bubble {
    width: 100%;
    flex: 6.5;
    margin-bottom: 10px;
    max-height: 100%;
    overflow-y: hidden;

    .empty-chat {
      display: flex;
      flex-direction: column;
      gap: 3vh;
    }

    .chat-with-messages {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow-y: auto;
      padding: 8px 10px;
    }

    & > * {
      width: 100%;
    }

    .magic-icon {
      width: 14vw;
      height: 14vh;
      color: var(--md-theme-default-primary);
    }

    .chat-explanation {
      color: var(--md-theme-default-primary);
      text-align: center;
      font-size: 1.6vw;
      font-style: normal;
      font-weight: 400;
      line-height: 32px;
    }

    .chat-cards {
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: 10px;

      .tip-card {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: flex-start;
        border-radius: 12px;
        cursor: pointer;
        gap: 5px;
        padding: 10px;
        color: var(--md-theme-default-primary);
        width: 240px;
        border: 1px solid #D9E2FF;
        background: #FFF;
        box-shadow: 0px 2px 10px 0px rgba(140, 183, 251, 0.40);

        i {
          margin: 0;
        }

        span {
          display: flex;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: 0.01px;
          text-align: left;
        }

        &:hover {
          background-color: #D9E2FF;
        }
      }
    }
  }
}

.chat {
  width: 100%;
  max-height: 15vh;
  flex: 2.5;
  position: relative;

  .text-input {
    width: 100%;
    height: 100%;
    text-align: left;
    vertical-align: top;
    padding: 10px 45px 10px 10px;
    line-height: normal;
    border-radius: 12px;
    border: #ACABAF;
    box-shadow: 0px 2px 10px 0px rgba(140, 183, 251, 0.40);
    color: #0E47A0;
    font-family: "WorkSans";
    font-feature-settings: 'liga' off, 'clig' off;
    font-size: 1.2em;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.01px;

    &:focus-visible {
      outline: #0E47A0 solid 2px;
    }
  }

  i {
    position: absolute;
    bottom: 15px;
    right: 15px;
    cursor: pointer;
    z-index: 10000;

    &:hover {
      color: #0E47A0;
    }
  }
}

.card {
  width: 43vw;
  color: #0E47A0 !important;
  font-family: "WorkSans";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.01px;
  font-feature-settings: 'liga' off, 'clig' off;
  border-radius: 12px;
  border: 1px solid #D9E2FF;
  box-shadow: 0px 2px 10px 0px rgba(140, 183, 251, 0.40);

  .card-content {
    padding: 10px !important;
    text-align: left;

    .action-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: 5px;

      .icon {
        margin: 0;
      }
    }
  }
}

.user-message {
  align-self: flex-end !important;
  margin-top: 30px;
  margin-bottom: 20px;

  .card {
    background-color: #D9E2FF !important;
  }
}

.ai-message {
  align-self: flex-start !important;
  margin-top: 10px;

  .card {
    background-color: #FFF !important;
  }
}

.icon-svg {
  width: 16px;
  height: 16px;
  -webkit-mask-size: contain;
  mask-size: contain;
  display: inline-block;
  cursor: pointer;
  background-color: #0000009e;

  &:hover {
    background-color: var(--md-theme-default-primary);
  }
}

.copied-svg {
  -webkit-mask: url('~@/assets/svg/neoAi/content-copy.svg') no-repeat center;
  mask: url('~@/assets/svg/neoAi/content-copy.svg') no-repeat center;
}

.download-svg {
  -webkit-mask: url('~@/assets/svg/neoAi/download.svg') no-repeat center;
  mask: url('~@/assets/svg/neoAi/download.svg') no-repeat center;
}

.preserve-linebreaks {
  white-space: pre-line;
}

.loading-dots {
  display: inline-block;

  span {
    animation: loading-dots 1.5s infinite;
    animation-fill-mode: both;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.3s;
    }

    &:nth-child(3) {
      animation-delay: 0.6s;
    }
  }
}

@keyframes loading-dots {
  0%, 80%, 100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
}
</style>
